import React from 'react';
import { Card, CardContent } from './ui/card';
import { CheckCircle, Zap, Users, Settings, Phone, Mail, MapPin } from 'lucide-react';
import { mockData } from '../mock';

const About = () => {
  const valueIcons = {
    'Zuverlässigkeit': CheckCircle,
    'Präzision': Settings,
    'Moderne Technik': Zap,
    'Persönliche Betreuung': Users
  };

  return (
    <section id="about" className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Über uns
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ihr vertrauensvoller Partner für alle elektrischen Anlagen in Reichling und Umgebung
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-12 items-start">
            {/* Left: Image (3/5 = größer) */}
            <div className="lg:col-span-3">
              <div className="relative">
                {/* Firmenwagen Elektrotechnik Wegele */}
                <div className="aspect-[4/3] rounded-2xl overflow-hidden border-4 border-white shadow-2xl bg-gray-100">
                  <img
                    src="/images/about-van.webp"
                    alt="Firmenfahrzeug Elektrotechnik Wegele in Reichling"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                {/* Floating Badge */}
                <div className="absolute -bottom-5 -right-5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-2xl shadow-xl">
                  <div className="text-center">
                    <div className="font-bold text-lg">Staatlich geprüft</div>
                    <div className="text-sm opacity-90">Elektrotechniker</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Content (2/5) */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  {mockData.company.owner}
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {mockData.aboutUs.description}
                </p>
              </div>

              {/* Values Grid */}
              <div className="grid sm:grid-cols-2 gap-6">
                {mockData.aboutUs.values.map((value, index) => {
                  const IconComponent = valueIcons[value.title] || CheckCircle;
                  return (
                    <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                            <IconComponent className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">{value.title}</h4>
                            <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Full-width Direct Contact */}
          <div className="mt-16 bg-gradient-to-r from-[#0a1632] to-[#1a2642] rounded-3xl p-8 md:p-12 text-white shadow-2xl">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div>
                <h4 className="font-bold text-2xl md:text-3xl mb-2">Direkter Kontakt</h4>
                <p className="text-white/70 text-base md:text-lg">Sprechen Sie uns an – wir freuen uns auf Ihre Anfrage.</p>
              </div>
              <div className="grid sm:grid-cols-3 gap-6 flex-1 md:max-w-3xl">
                <a href={`tel:${mockData.contact.phone.replace(/\s/g, '')}`} className="flex items-center gap-4 group" data-testid="about-contact-phone">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-white/60 mb-1">Telefon</div>
                    <div className="font-semibold group-hover:text-cyan-400 transition-colors">{mockData.contact.phone}</div>
                  </div>
                </a>
                <a href={`mailto:${mockData.contact.email}`} className="flex items-center gap-4 group" data-testid="about-contact-email">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Mail className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-white/60 mb-1">E-Mail</div>
                    <div className="font-semibold group-hover:text-cyan-400 transition-colors break-all">{mockData.contact.email}</div>
                  </div>
                </a>
                <div className="flex items-center gap-4" data-testid="about-contact-address">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-white/60 mb-1">Standort</div>
                    <div className="font-semibold">{mockData.contact.address}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;